/**
 * AnimalTile Component
 *
 * A draggable tile displaying an animal emoji for the password system.
 * Kids drag these into drop boxes to enter their password.
 */

import { memo } from 'react';
import { Draggable } from '../DragDrop';
import type { DragItem } from '../DragDrop';
import {
  type AnimalType,
  type AnimalTileProps,
  type AnimalTileDragData,
  getAnimalEmoji,
  getAnimalLabel,
} from './types';

/**
 * Drag item type for animal tiles
 */
export const ANIMAL_DRAG_TYPE = 'ANIMAL_TILE';

/**
 * Create a drag item for an animal tile
 */
function createAnimalDragItem(animal: AnimalType): DragItem {
  const data: AnimalTileDragData = { type: 'animal', animal };
  return {
    type: ANIMAL_DRAG_TYPE,
    id: `animal-${animal}`,
    data,
  };
}

/**
 * Size classes for different tile sizes
 */
const SIZE_CLASSES = {
  sm: 'w-16 h-16 text-2xl',
  md: 'w-20 h-20 text-3xl',
  lg: 'w-24 h-24 text-4xl',
} as const;

/**
 * AnimalTile - Draggable emoji tile for password entry
 * Supports both drag-and-drop AND tap-and-tap input modes
 */
export const AnimalTile = memo(function AnimalTile({
  animal,
  draggable = true,
  disabled = false,
  isSelected = false,
  onClick,
  size = 'md',
  className = '',
  testId,
}: AnimalTileProps) {
  const emoji = getAnimalEmoji(animal);
  const label = getAnimalLabel(animal);
  const sizeClasses = SIZE_CLASSES[size];

  // Handle click for tap-and-tap mode
  const handleClick = () => {
    if (!disabled && onClick) {
      onClick(animal);
    }
  };

  const tileContent = (
    <div
      className={`
        ${sizeClasses}
        flex items-center justify-center
        bg-white rounded-xl border-3
        shadow-md hover:shadow-lg
        transition-all duration-150
        select-none
        ${isSelected
          ? 'border-magic-500 ring-2 ring-magic-400 scale-110 bg-magic-50'
          : 'border-neutral-200'}
        ${disabled
          ? 'opacity-50 cursor-not-allowed'
          : onClick
            ? 'cursor-pointer hover:border-magic-300'
            : 'cursor-grab active:cursor-grabbing'}
        ${className}
      `}
      role={onClick ? 'button' : 'img'}
      aria-label={isSelected ? `${label} (selected) - tap a box to place` : label}
      aria-pressed={onClick ? isSelected : undefined}
      onClick={handleClick}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      } : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      data-testid={testId ?? `animal-tile-${animal}`}
      data-selected={isSelected}
    >
      <span className="drop-shadow-sm">{emoji}</span>
    </div>
  );

  // If not draggable or disabled, just render the content
  if (!draggable || disabled) {
    return tileContent;
  }

  // Wrap in Draggable for drag-and-drop functionality
  return (
    <Draggable
      item={createAnimalDragItem(animal)}
      canDrag={() => !disabled}
      ariaLabel={`Drag ${label} to a box, or tap to select`}
      testId={testId ?? `animal-tile-${animal}`}
    >
      {tileContent}
    </Draggable>
  );
});

AnimalTile.displayName = 'AnimalTile';

export default AnimalTile;
